import { createTheme, alpha } from '@mui/material/styles';

/**
 * Brand tokens from yogamelieluberon.fr (Elementor homepage).
 * Burgundy #942B0D, beige #FBF3EA, teal accent #016E75,
 * fonts Alata (headings) + Outfit (body).
 */
const brand = {
  burgundy: '#942B0D',
  burgundyDark: '#741F07',
  burgundyLight: '#B84A2A',
  burgundySoft: '#8F3A3A',
  beige: '#FBF3EA',
  beigePaper: '#FFF9F3',
  beigeMuted: '#F3E6D8',
  teal: '#016E75',
  tealDark: '#014F54',
  tealLight: '#268088',
  tealInk: '#26474A',
  text: '#2A1A14',
  textMuted: '#5C4033',
  error: '#AD2C2C',
  success: '#4A6B5C',
  white: '#FFFFFF',
};

const theme = createTheme({
  palette: {
    primary: {
      main: brand.burgundy,
      light: brand.burgundyLight,
      dark: brand.burgundyDark,
      lighter: alpha(brand.burgundy, 0.12),
      contrastText: brand.white,
    },
    secondary: {
      main: brand.teal,
      light: brand.tealLight,
      dark: brand.tealDark,
      contrastText: brand.white,
    },
    background: {
      default: brand.beige,
      paper: brand.beigePaper,
      neutral: brand.beigeMuted,
    },
    text: {
      primary: brand.text,
      secondary: brand.textMuted,
    },
    success: {
      main: brand.success,
      light: '#6A8B7A',
      dark: '#3A5548',
      contrastText: brand.white,
    },
    error: {
      main: brand.error,
      light: '#C45A5A',
      dark: '#8A2222',
      contrastText: brand.white,
    },
    info: {
      main: brand.teal,
      light: brand.tealLight,
      dark: brand.tealDark,
      contrastText: brand.white,
    },
    divider: alpha(brand.burgundy, 0.16),
  },
  typography: {
    fontFamily: '"Outfit", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Alata", "Outfit", sans-serif',
      fontWeight: 400,
      color: brand.text,
    },
    h2: {
      fontFamily: '"Alata", "Outfit", sans-serif',
      fontWeight: 400,
      color: brand.text,
    },
    h3: {
      fontFamily: '"Alata", "Outfit", sans-serif',
      fontWeight: 400,
      color: brand.text,
    },
    h4: {
      fontFamily: '"Alata", "Outfit", sans-serif',
      fontWeight: 400,
      color: brand.text,
    },
    h5: {
      fontFamily: '"Alata", "Outfit", sans-serif',
      fontWeight: 400,
      color: brand.text,
    },
    h6: {
      fontFamily: '"Alata", "Outfit", sans-serif',
      fontWeight: 400,
      color: brand.text,
    },
    subtitle1: {
      fontFamily: '"Outfit", sans-serif',
      color: brand.textMuted,
    },
    subtitle2: {
      fontFamily: '"Outfit", sans-serif',
      color: brand.textMuted,
    },
    body1: {
      fontFamily: '"Outfit", sans-serif',
      color: brand.text,
    },
    body2: {
      fontFamily: '"Outfit", sans-serif',
      color: brand.textMuted,
    },
    button: {
      fontFamily: '"Outfit", sans-serif',
      fontWeight: 500,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: brand.beige,
          color: brand.text,
        },
        a: {
          color: brand.burgundy,
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
          fontFamily: '"Outfit", sans-serif',
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          backgroundColor: brand.burgundy,
          '&:hover': {
            backgroundColor: brand.burgundyDark,
          },
        },
        containedSecondary: {
          backgroundColor: brand.teal,
          '&:hover': {
            backgroundColor: brand.tealDark,
          },
        },
        outlinedPrimary: {
          borderColor: brand.burgundy,
          color: brand.burgundy,
          '&:hover': {
            borderColor: brand.burgundyDark,
            backgroundColor: alpha(brand.burgundy, 0.06),
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          backgroundColor: brand.beigePaper,
          boxShadow: `0 4px 16px ${alpha(brand.burgundy, 0.08)}`,
          border: `1px solid ${alpha(brand.burgundy, 0.08)}`,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: brand.beigePaper,
          color: brand.text,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: brand.burgundy,
          '&:hover': {
            color: brand.burgundyDark,
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
  },
});

export { brand };
export default theme;
